import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Clock, Shield, ArrowUpRight } from "lucide-react";

const cases = [
  {
    sector: "BANQUE · 1 200 collaborateurs",
    title: "Certifiée ISO 27001 en 9 mois",
    challenge:
      "Refonte complète du SMSI, mise en conformité PCI-DSS et préparation à l'audit de certification.",
    results: [
      { value: "9", label: "MOIS", sub: "Audit → certification" },
      { value: "-42%", label: "RISQUES", sub: "Résiduels mesurés" },
      { value: "100%", label: "CONTRÔLES", sub: "Annexe A couverts" },
    ],
    tag: "Gouvernance · ISO 27001",
    color: "emerald",
  },
  {
    sector: "FINTECH AFRIQUE DE L'OUEST · 250 personnes",
    title: "SOC managé 24/7 déployé en 6 semaines",
    challenge:
      "Mise en place d'un SOC externalisé, intégration SIEM + EDR sur 800 endpoints, threat hunting actif.",
    results: [
      { value: "6", label: "SEMAINES", sub: "Mise en service complète" },
      { value: "24/7", label: "DÉTECTION", sub: "MTTR < 30 min" },
      { value: "+18", label: "INCIDENTS", sub: "Bloqués 1er mois" },
    ],
    tag: "SOC · SIEM · EDR",
    color: "emerald",
  },
  {
    sector: "ETI INDUSTRIELLE · 3 sites EU",
    title: "Conformité NIS 2 atteinte avant échéance",
    challenge:
      "Cartographie OIV, plan de remédiation 60 actions, gestion des accès à privilèges PAM industriel.",
    results: [
      { value: "60", label: "ACTIONS", sub: "Priorisées & livrées" },
      { value: "0", label: "ÉCART", sub: "Sur audit NIS2" },
      { value: "-60%", label: "COMPTES", sub: "À privilèges actifs" },
    ],
    tag: "NIS 2 · PAM · OT",
    color: "emerald",
  },
];

export default function CaseStudies() {
  return (
    <section id="cas-clients" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-8">
            <div className="font-mono text-xs text-emerald-400 mb-4 tracking-widest">
              // ÉTUDES DE CAS
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Des résultats <span className="italic text-emerald-400">mesurables.</span>
              <br />
              Pas des promesses.
            </h2>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="text-gray-400 leading-relaxed">
              Trois missions anonymisées récentes. Les chiffres parlent à votre place
              auprès de votre comité de direction.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {cases.map((c, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`case-study-${i}`}
              className="group relative p-7 rounded-3xl bg-[#0C1410] border border-[#1A2E22] hover:border-emerald-500/40 transition-all hover:-translate-y-1 hover:shadow-[0_24px_60px_-20px_rgba(16,185,129,0.25)] flex flex-col"
            >
              <div className="font-mono text-[10px] text-gray-500 tracking-widest mb-4">
                {c.sector}
              </div>
              <h3 className="font-display text-xl md:text-2xl font-semibold leading-tight mb-3">
                {c.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-6">
                {c.challenge}
              </p>

              <div className="grid grid-cols-3 gap-2 mt-auto mb-5 pt-5 border-t border-[#1A2E22]">
                {c.results.map((r, j) => (
                  <div key={j}>
                    <div className="font-display text-2xl md:text-3xl font-bold text-emerald-400 leading-none">
                      {r.value}
                    </div>
                    <div className="font-mono text-[9px] text-gray-500 tracking-widest mt-1.5">
                      {r.label}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">
                      {r.sub}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono text-emerald-400">
                  ▸ {c.tag}
                </span>
                <ArrowUpRight className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <p className="text-sm text-gray-400">
            Un cas similaire au vôtre ?
          </p>
          <a
            href="#contact"
            data-testid="case-studies-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/40 hover:border-emerald-500 hover:bg-emerald-500/5 text-emerald-400 text-sm font-medium transition-all"
          >
            Demander une étude personnalisée
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
